// Formatting + summary helpers for Time Study UI / CSV export.

import type { TimeStudyEntry } from "@/lib/manufacturing/timeStudyClient";

export function formatDurationSeconds(totalSeconds: number): string {
  const s = Math.max(0, Math.round(totalSeconds));
  const h = Math.floor(s / 3600);
  const m = Math.floor((s % 3600) / 60);
  const rem = s % 60;
  if (h > 0) return rem ? `${h}h ${m}m ${rem}s` : m ? `${h}h ${m}m` : `${h}h`;
  if (m > 0) return rem ? `${m}m ${rem}s` : `${m}m`;
  return `${rem}s`;
}

export function durationSecondsToMinutesInput(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds <= 0) return "";
  const mins = seconds / 60;
  return Number.isInteger(mins) ? String(mins) : mins.toFixed(2);
}

export function minutesInputToSeconds(raw: string): number | null {
  const n = Number(raw);
  if (!Number.isFinite(n) || n <= 0) return null;
  return Math.round(n * 60);
}

export type TimeStudyBucketTotal = {
  key: string;
  label: string;
  count: number;
  durationSeconds: number;
};

function bump(
  map: Map<string, TimeStudyBucketTotal>,
  key: string,
  label: string,
  seconds: number,
) {
  const cur = map.get(key);
  if (cur) {
    cur.count += 1;
    cur.durationSeconds += seconds;
  } else {
    map.set(key, { key, label, count: 1, durationSeconds: seconds });
  }
}

export function summarizeTimeStudyEntries(entries: TimeStudyEntry[]): {
  bySubject: TimeStudyBucketTotal[];
  byPersonnel: TimeStudyBucketTotal[];
  byDay: TimeStudyBucketTotal[];
  totalSeconds: number;
} {
  const bySubject = new Map<string, TimeStudyBucketTotal>();
  const byPersonnel = new Map<string, TimeStudyBucketTotal>();
  const byDay = new Map<string, TimeStudyBucketTotal>();
  let totalSeconds = 0;

  for (const e of entries) {
    const sec = e.duration_seconds;
    totalSeconds += sec;
    bump(bySubject, e.subject_label.toLowerCase(), e.subject_label, sec);
    const person = e.personnel_label?.trim() || "(unassigned)";
    bump(byPersonnel, person.toLowerCase(), person, sec);
    const day = (e.occurred_at || e.created_at || "").slice(0, 10) || "unknown";
    bump(byDay, day, day, sec);
  }

  const sortDesc = (a: TimeStudyBucketTotal, b: TimeStudyBucketTotal) =>
    b.durationSeconds - a.durationSeconds;

  return {
    bySubject: [...bySubject.values()].sort(sortDesc),
    byPersonnel: [...byPersonnel.values()].sort(sortDesc),
    byDay: [...byDay.values()].sort((a, b) => a.key.localeCompare(b.key)),
    totalSeconds,
  };
}

function csvEscape(v: string | number | null | undefined): string {
  const s = v == null ? "" : String(v);
  if (/[",\n\r]/.test(s)) return `"${s.replace(/"/g, '""')}"`;
  return s;
}

export function timeStudyEntriesToCsv(entries: TimeStudyEntry[]): string {
  const header = [
    "occurred_at",
    "subject_label",
    "entry_kind",
    "duration_seconds",
    "duration_minutes",
    "personnel_label",
    "quantity",
    "quantity_unit",
    "notes",
    "source",
    "recipe_id",
    "build_model_id",
    "plant_id",
    "unit_id",
  ];
  const rows = entries.map((e) =>
    [
      e.occurred_at,
      e.subject_label,
      e.entry_kind,
      e.duration_seconds,
      (e.duration_seconds / 60).toFixed(2),
      e.personnel_label,
      e.quantity,
      e.quantity_unit,
      e.notes,
      e.source,
      e.recipe_id,
      e.build_model_id,
      e.plant_id,
      e.unit_id,
    ]
      .map(csvEscape)
      .join(","),
  );
  return [header.join(","), ...rows].join("\n");
}

export function downloadTextFile(filename: string, contents: string, mime = "text/csv") {
  const blob = new Blob([contents], { type: `${mime};charset=utf-8` });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
